/**
 * Strict numeric input sanitizer for currency / numeric form fields.
 *
 * Default `parseFloat` is too lenient for form input: `parseFloat("12-34")`
 * returns 12 and `parseFloat("1.2.3")` returns 1.2 — silently truncating
 * malformed values that should have failed validation. For currency fields
 * (insurance premiums, asset purchase prices) this would silently save the
 * wrong dollar amount with no error surfaced to the user.
 *
 * Returns the parsed number on success, or `null` on:
 *   - empty / null / undefined input
 *   - structural malformations (multiple ".", multiple "-", or "-" not
 *     at the leading position)
 *   - any input parseFloat rejects after we strip currency symbols
 *
 * Accepts:
 *   - "$1,200.50"  → 1200.5  (currency-formatted input round-trips)
 *   - "0.5"        → 0.5
 *   - "-100"       → -100
 *
 * Rejects:
 *   - "12-34"      → null   (parseFloat would return 12)
 *   - "1.2.3"      → null   (parseFloat would return 1.2)
 *   - "--5"        → null
 *   - "1-2"        → null   (- not leading)
 */
export function sanitizeNumeric(val) {
    if (val === null || val === undefined || val === '') return null;
    const cleaned = val.toString().replace(/[^0-9.-]/g, '');
    if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
    if ((cleaned.match(/\./g) || []).length > 1) return null;
    if ((cleaned.match(/-/g) || []).length > 1) return null;
    if (cleaned.includes('-') && !cleaned.startsWith('-')) return null;
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Format a value as a fixed two-decimal money string for display.
 *
 *   formatMoney('242854')   → '242854.00'
 *   formatMoney('1738.8')   → '1738.80'
 *   formatMoney(-4469.2)    → '-4469.20'
 *   formatMoney('')         → ''        (empty stays empty — don't force 0.00)
 *   formatMoney('abc')      → ''
 *
 * Reuses sanitizeNumeric so currency symbols / commas / partial input
 * ("100.") all normalize. Returns '' for empty or unparseable input.
 */
export function formatMoney(val) {
    const n = sanitizeNumeric(val);
    return n == null ? '' : n.toFixed(2);
}

/**
 * Round to cents, half away from zero, without binary-float error:
 * `Math.round(1.005 * 100)` is 100 (1.005 is stored as 1.00499…), so shift
 * the decimal point in the number's string form instead.
 *
 *   roundCents(1.005)    → 1.01
 *   roundCents(1925.325) → 1925.33
 *   roundCents(-41.505)  → -41.51
 */
export function roundCents(n) {
    const shifted = Math.round(Number(`${Math.abs(n)}e2`));
    // Exponent-form input ("1e21") can't take a second exponent; fall back.
    const cents = Number.isFinite(shifted) ? shifted : Math.round(Math.abs(n) * 100);
    return (Math.sign(n) * cents) / 100;
}

/**
 * A money value the API will accept, or null: parsed like formatMoney, rounded
 * to cents, non-negative unless allowed, and within a 12-digit / 2-decimal
 * column.
 *
 *   toSubmittableMoney(1925.325)      → 1925.33
 *   toSubmittableMoney('$1,200')      → 1200
 *   toSubmittableMoney(-572.2)        → null
 *   toSubmittableMoney(-41.5, { allowNegative: true }) → -41.5
 *
 * For values a fix-it card sends WITHOUT showing them: if the server rejected
 * one, the user would have no input to correct and the card could never save.
 */
export function toSubmittableMoney(val, { allowNegative = false } = {}) {
    const n = sanitizeNumeric(val);
    if (n == null || (!allowNegative && n < 0)) return null;
    const rounded = roundCents(n);
    return Math.abs(rounded) < 1e10 ? rounded : null;
}

/**
 * Constrain a money field's value *while typing*: digits, a single decimal
 * point, at most two decimal places, and an optional leading minus (only when
 * `allowNegative`). Leaves partial input intact ('', '100', '100.', '-') so the
 * user can keep editing; final 2-decimal normalization happens on blur via
 * formatMoney.
 *
 *   sanitizeMoneyKeystroke('1,234.567')         → '1234.56'
 *   sanitizeMoneyKeystroke('-50.5', true)        → '-50.5'
 *   sanitizeMoneyKeystroke('-50', false)         → '50'
 */
export function sanitizeMoneyKeystroke(raw, allowNegative = false) {
    let s = String(raw ?? '').replace(allowNegative ? /[^\d.-]/g : /[^\d.]/g, '');
    const negative = allowNegative && s.startsWith('-');
    s = s.replace(/-/g, '');
    const [intPart = '', ...rest] = s.split('.');
    let out = intPart;
    if (rest.length > 0) {
        out += '.' + rest.join('').slice(0, 2);
    }
    return (negative ? '-' : '') + out;
}
